import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { scheduleFailureReason, scheduleNeedsAttention } from '@canopy/shared';
import { useReportTemplates } from '../api/report-templates.js';
import { useDashboards } from '../api/dashboards.js';
import { useAllSchedules } from '../api/schedules.js';
import { useFrameworks } from '../api/frameworks.js';
import { useDrillTargets } from '../api/drill-targets.js';

/**
 * HomePage — the "where am I, what's going on?" landing surface.
 *
 * Prioritizes immediate context over navigation: what changed
 * recently, what needs attention, what's scheduled to fire, and
 * quick ways into the product's authoring surfaces.
 *
 * Every block here is a thin composition of existing hooks; nothing
 * new server-side.
 */
export function HomePage(): JSX.Element {
  const { data: templates } = useReportTemplates();
  const { data: dashboards } = useDashboards();
  const { data: schedules } = useAllSchedules();
  const { data: frameworks } = useFrameworks();
  const { data: drillTargets } = useDrillTargets();

  const counts = {
    templates: templates?.length ?? 0,
    dashboards: dashboards?.length ?? 0,
    schedules: schedules?.length ?? 0,
    frameworks: frameworks?.length ?? 0,
    drillTargets: drillTargets?.length ?? 0,
  };

  const recent = useMemo(() => recentActivity(templates, dashboards), [templates, dashboards]);
  const upcoming = useMemo(() => upcomingSchedules(schedules ?? []), [schedules]);
  // CSAGE-346: classify via @canopy/shared — this used to exclude only
  // 'success'/'pending', neither of which the runner ever writes, so
  // successful schedules were listed as failing.
  const failing = useMemo(
    () => (schedules ?? []).filter((s) => s.enabled && scheduleNeedsAttention(s.lastStatus)),
    [schedules],
  );

  return (
    <div className="home-page">
      <header className="home-hero">
        <div>
          <h1>Canopy Sage</h1>
          <p className="text-dim">
            Your reporting and analytics workbench — templates, dashboards, frameworks, and
            scheduled delivery. Built to be driven by people in the web UI or by agents via the
            Claude Code MCP server.
          </p>
        </div>
        <div className="home-hero-actions">
          <Link to="/templates" className="btn btn--primary">
            + New report template
          </Link>
          <Link to="/insights" className="btn btn--ghost">
            + New dashboard
          </Link>
        </div>
      </header>

      <div className="home-stats">
        <StatCard label="Templates" value={counts.templates} to="/templates" />
        <StatCard label="Dashboards" value={counts.dashboards} to="/insights" />
        <StatCard label="Schedules" value={counts.schedules} to="/admin/schedules" />
        <StatCard
          label="Frameworks"
          value={counts.frameworks}
          to="/templates"
          hint="authoring starters"
        />
        <StatCard label="Drill targets" value={counts.drillTargets} to="/admin/drill-targets" />
      </div>

      <div className="home-grid">
        {/* Upcoming schedules */}
        <section className="home-card">
          <header>
            <h2>Next up</h2>
            <Link to="/admin/schedules" className="text-sm">
              All schedules →
            </Link>
          </header>
          {upcoming.length === 0 ? (
            <div className="home-empty">No schedules firing in the next 24 hours.</div>
          ) : (
            <ul className="home-list">
              {upcoming.slice(0, 6).map((s) => (
                <li key={s.id}>
                  <span className="home-list-lead">{formatRelative(s.nextRunAt)}</span>
                  <span className="home-list-main">{s.name}</span>
                  <span className="home-list-meta">
                    {s.delivery.channels.map((c) => c.kind).join(', ')}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Failing schedules — only show if non-empty */}
        {failing.length > 0 && (
          <section className="home-card home-card--danger">
            <header>
              <h2>Needs attention</h2>
              <Link to="/admin/schedules" className="text-sm">
                Investigate →
              </Link>
            </header>
            <ul className="home-list">
              {failing.slice(0, 5).map((s) => (
                <li key={s.id}>
                  {/* The raw value is `failed:<reason>`; show the reason,
                      not the prefix, and keep the full text on hover. */}
                  <span
                    className="badge badge--danger"
                    title={scheduleFailureReason(s.lastStatus) ?? s.lastStatus ?? ''}
                  >
                    {scheduleFailureReason(s.lastStatus) ?? s.lastStatus}
                  </span>
                  <span className="home-list-main">{s.name}</span>
                  <span className="home-list-meta">last run {formatRelative(s.lastRunAt)}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Recent authoring activity */}
        <section className="home-card">
          <header>
            <h2>Recent activity</h2>
          </header>
          {recent.length === 0 ? (
            <div className="home-empty">
              Nothing yet — create a template or dashboard to get started.
            </div>
          ) : (
            <ul className="home-list">
              {recent.slice(0, 8).map((item) => (
                <li key={`${item.kind}-${item.id}`}>
                  <span
                    className={'badge badge--' + (item.kind === 'template' ? 'accent' : 'success')}
                  >
                    {item.kind}
                  </span>
                  <span className="home-list-main">
                    <Link to={item.href}>{item.name}</Link>
                  </span>
                  <span className="home-list-meta">{formatRelative(item.updatedAt)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Claude Code — discoverable entry point */}
        <section className="home-card home-card--accent">
          <header>
            <h2>Use this with Claude Code</h2>
          </header>
          <p className="text-sm">
            An MCP server ships with this platform. Mint an API key, paste the config, and agents
            can list frameworks, validate layouts, preview renders, and publish on your behalf —
            with the same audit trail as web actions.
          </p>
          <div className="row" style={{ gap: 8 }}>
            <Link to="/admin/claude-code" className="btn btn--primary btn--sm">
              Setup guide
            </Link>
            <Link to="/admin/api-keys" className="btn btn--ghost btn--sm">
              Manage keys
            </Link>
          </div>
        </section>

        {/* Quick tour of what's where */}
        <section className="home-card">
          <header>
            <h2>Places to go</h2>
          </header>
          <div className="home-tiles">
            <HomeTile
              title="Templates"
              desc="HTML / Handlebars templates with audience overlays and page options"
              to="/templates"
            />
            <HomeTile
              title="Reports"
              desc="Configured, schedulable instances of a template — bound to a customer"
              to="/reports"
            />
            <HomeTile
              title="Insights"
              desc="Drag-and-drop widget dashboards and kiosk walls"
              to="/insights"
            />
            <HomeTile
              title="Standards"
              desc="Helper catalog, widget types, audience rules"
              to="/admin/standards"
            />
            <HomeTile
              title="Drill-through"
              desc="Register reusable links with parameter contracts"
              to="/admin/drill-targets"
            />
            <HomeTile
              title="Schedules"
              desc="Cron + email / webhook delivery with per-run status"
              to="/admin/schedules"
            />
            <HomeTile
              title="Audit"
              desc="Every action across web + MCP, tenant-isolated"
              to="/admin/audit"
            />
          </div>
        </section>
      </div>

      <HomeStyles />
    </div>
  );
}

function StatCard({
  label,
  value,
  to,
  hint,
}: {
  label: string;
  value: number;
  to: string;
  hint?: string;
}): JSX.Element {
  return (
    <Link to={to} className="home-stat">
      <div className="home-stat-value">{value.toLocaleString()}</div>
      <div className="home-stat-label">{label}</div>
      {hint && <div className="home-stat-hint">{hint}</div>}
    </Link>
  );
}

function HomeTile({ title, desc, to }: { title: string; desc: string; to: string }): JSX.Element {
  return (
    <Link to={to} className="home-tile">
      <div className="home-tile-title">{title}</div>
      <div className="home-tile-desc">{desc}</div>
    </Link>
  );
}

// ─── Data shaping ───────────────────────────────────────────────────────

type ActivityItem = {
  kind: 'template' | 'dashboard';
  id: string;
  name: string;
  href: string;
  updatedAt: string;
};

function recentActivity(
  templates: Array<{ id: string; name: string; updatedAt: string }> | undefined,
  dashboards: Array<{ id: string; name: string; updatedAt: string }> | undefined,
): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const t of templates ?? []) {
    items.push({
      kind: 'template',
      id: t.id,
      name: t.name,
      href: `/templates/${t.id}`,
      updatedAt: t.updatedAt,
    });
  }
  for (const d of dashboards ?? []) {
    items.push({
      kind: 'dashboard',
      id: d.id,
      name: d.name,
      href: `/insights/${d.id}`,
      updatedAt: d.updatedAt,
    });
  }
  items.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  return items;
}

function upcomingSchedules(
  list: Array<{
    id: string;
    name: string;
    nextRunAt: string | null;
    delivery: { channels: Array<{ kind: string }> };
    enabled: boolean;
    lastStatus: string | null;
  }>,
): typeof list {
  const cutoff = Date.now() + 24 * 60 * 60 * 1000;
  return list
    .filter((s) => s.enabled && s.nextRunAt && new Date(s.nextRunAt).getTime() <= cutoff)
    .sort((a, b) => new Date(a.nextRunAt!).getTime() - new Date(b.nextRunAt!).getTime());
}

function formatRelative(iso: string | null): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const diffSec = Math.round((t - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  const dir = diffSec > 0 ? 'in ' : '';
  const suffix = diffSec > 0 ? '' : ' ago';
  if (abs < 60) return `${dir}${abs}s${suffix}`;
  if (abs < 3600) return `${dir}${Math.round(abs / 60)}m${suffix}`;
  if (abs < 86400) return `${dir}${Math.round(abs / 3600)}h${suffix}`;
  return new Date(iso).toLocaleString();
}

function HomeStyles(): JSX.Element {
  return (
    <style>{`
      .home-page {
        display: flex;
        flex-direction: column;
        gap: var(--space-4);
        padding: 0;
      }
      .home-hero {
        display: flex;
        align-items: flex-end;
        justify-content: space-between;
        gap: var(--space-4);
        padding: var(--space-5) 0 var(--space-3);
      }
      .home-hero h1 {
        margin: 0 0 6px;
        font-size: 32px;
        font-weight: 700;
        letter-spacing: -0.02em;
      }
      .home-hero p {
        max-width: 680px;
        margin: 0;
        line-height: 1.5;
      }
      .home-hero-actions {
        display: flex;
        gap: 8px;
      }
      .home-stats {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
        gap: var(--space-2);
      }
      .home-stat {
        display: block;
        padding: var(--space-3) var(--space-4);
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        text-decoration: none;
        color: inherit;
        transition: border-color 0.12s, transform 0.12s;
      }
      .home-stat:hover {
        border-color: var(--color-accent);
        transform: translateY(-1px);
      }
      .home-stat-value {
        font-size: 28px;
        font-weight: 700;
        letter-spacing: -0.02em;
        line-height: 1.1;
      }
      .home-stat-label {
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.08em;
        color: var(--color-text-secondary);
        margin-top: 4px;
      }
      .home-stat-hint {
        font-size: 11px;
        color: var(--color-text-dim);
        margin-top: 2px;
      }
      .home-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        gap: var(--space-3);
      }
      .home-card {
        background: var(--color-surface-1);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-md);
        padding: var(--space-4);
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
      }
      .home-card header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
      }
      .home-card h2 {
        margin: 0;
        font-size: var(--font-size-md);
        font-weight: 600;
      }
      .home-card--accent {
        border-color: color-mix(in srgb, var(--color-accent) 35%, transparent);
        background: color-mix(in srgb, var(--color-accent) 6%, var(--color-surface-1));
      }
      .home-card--danger {
        border-color: color-mix(in srgb, var(--color-danger) 35%, transparent);
      }
      .home-list {
        list-style: none;
        padding: 0;
        margin: 0;
        display: flex;
        flex-direction: column;
        gap: 6px;
      }
      .home-list li {
        display: grid;
        grid-template-columns: auto 1fr auto;
        gap: 10px;
        align-items: center;
        font-size: var(--font-size-sm);
      }
      .home-list-lead {
        font-size: 11px;
        color: var(--color-accent);
        font-weight: 600;
        white-space: nowrap;
      }
      .home-list-main {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      .home-list-meta {
        font-size: 11px;
        color: var(--color-text-dim);
        white-space: nowrap;
      }
      .home-empty {
        color: var(--color-text-dim);
        font-size: var(--font-size-sm);
        padding: var(--space-2) 0;
      }
      .home-tiles {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 8px;
      }
      .home-tile {
        display: block;
        padding: 10px 12px;
        background: var(--color-surface-2);
        border: 1px solid var(--color-border-subtle);
        border-radius: var(--radius-sm);
        text-decoration: none;
        color: inherit;
        transition: border-color 0.12s, transform 0.12s;
      }
      .home-tile:hover {
        border-color: var(--color-accent);
        transform: translateY(-1px);
      }
      .home-tile-title {
        font-size: var(--font-size-sm);
        font-weight: 600;
      }
      .home-tile-desc {
        font-size: 11px;
        color: var(--color-text-dim);
        margin-top: 2px;
        line-height: 1.35;
      }
    `}</style>
  );
}
